import { useMemo } from 'react';

import { Button, Chip, Stack, Tooltip } from '@mui/material';

import { PercentualNotaMetaSazonalAutoComplete } from 'components/autocompletes/PercentualNotaMetaSazonalAutoComplete';
import { Block } from 'components/page';
import { SubTitle } from 'components/text';
import { useMutateGeracaoConfigs } from 'pages/compra/configuracao-pedido/hooks/pedidos-sazonais/useMutateGeracaoConfigs';
import { useQueryStatusGeracao } from 'pages/compra/configuracao-pedido/hooks/pedidos-sazonais/useQueryStatusGeracao';
import { ConfigPedidoPedidosSazonais, LojaMetaAtingidaProps, StatusGeracaoConfig } from 'pages/compra/configuracao-pedido/types';

type ConfigPedidoGeracaoConfigs = {
    values: ConfigPedidoPedidosSazonais;
    initialValues: ConfigPedidoPedidosSazonais;
    setFieldValue: (field: string, value: any) => void;
};

export function ConfigPedidoGeracaoConfigs({ values, initialValues, setFieldValue }: ConfigPedidoGeracaoConfigs) {
    const { lojasMetaAtingida, percentualNotaRoyaltiesSazonal } = values;
    const { lojasMetaAtingida: initiallojasMetaAtingida, percentualNotaRoyaltiesSazonal: initialPercentual } = initialValues;

    const { statusGeracao, refetch } = useQueryStatusGeracao();
    const { gerarConfiguracoes, reverterConfiguracoes, isLoadingGerar, isLoadingReverter } = useMutateGeracaoConfigs({ refetch });

    const hasInitialPercentual = Boolean(initialPercentual && Object.keys(initialPercentual).length);

    const hasPercentualChanged = useMemo(() => {
        if (!hasInitialPercentual && !percentualNotaRoyaltiesSazonal) return false;
        if (!hasInitialPercentual || !percentualNotaRoyaltiesSazonal) return true;

        return initialPercentual?.idPercentualNotaRoyaltiesSazonal !== percentualNotaRoyaltiesSazonal.idPercentualNotaRoyaltiesSazonal;
    }, [hasInitialPercentual, percentualNotaRoyaltiesSazonal, initialPercentual]);

    const hasMetasChanged = useMemo(() => {
        if (!initiallojasMetaAtingida || !lojasMetaAtingida) return false;
        if (initiallojasMetaAtingida.length !== lojasMetaAtingida.length) return true;

        return initiallojasMetaAtingida.some((initialMetaLoja: LojaMetaAtingidaProps) => {
            const existMetaLoja = lojasMetaAtingida.find((metaLoja) => initialMetaLoja.loja.idLoja === metaLoja.loja.idLoja);

            return (
                !existMetaLoja ||
                existMetaLoja.fgMetaAtingida !== initialMetaLoja.fgMetaAtingida ||
                existMetaLoja.tipoLogisticaLoja?.idTipoLogisticaLoja !== initialMetaLoja.tipoLogisticaLoja?.idTipoLogisticaLoja
            );
        });
    }, [initiallojasMetaAtingida, lojasMetaAtingida]);

    const isStatusDiferenteRevertido = Boolean(statusGeracao && statusGeracao.idStatusGeracao !== StatusGeracaoConfig.REVERTIDO);

    const isStatusNaoFinalizado = Boolean(
        statusGeracao &&
            ![StatusGeracaoConfig.FINALIZADO_COM_FALHA, StatusGeracaoConfig.FINALIZADO_COM_SUCESSO].includes(statusGeracao.idStatusGeracao),
    );

    const tooltipGerarConfiguracoes = useMemo(() => {
        if (hasPercentualChanged || hasMetasChanged) {
            if (!percentualNotaRoyaltiesSazonal) {
                return 'É necessário informar um tipo de percentual de nota fiscal para habilitar a geração de configurações';
            }

            return 'É necessário salvar para habilitar a geração de configurações';
        }

        if (initiallojasMetaAtingida && initiallojasMetaAtingida.length === 0) {
            return 'É necessário informar ao menos uma meta de loja para habilitar a geração de configurações';
        }

        return null;
    }, [hasMetasChanged, hasPercentualChanged, initiallojasMetaAtingida, percentualNotaRoyaltiesSazonal]);

    const disableBtnGerar =
        isLoadingReverter ||
        !hasInitialPercentual ||
        hasPercentualChanged ||
        hasMetasChanged ||
        (initiallojasMetaAtingida && initiallojasMetaAtingida.length === 0) ||
        isStatusDiferenteRevertido;

    return (
        <>
            <Block>
                <SubTitle label="Percentual de nota fiscal" />

                <Tooltip title={isStatusDiferenteRevertido ? 'Para alterar o tipo de percentual, reverta a geração de configurações' : ''}>
                    <span>
                        <PercentualNotaMetaSazonalAutoComplete
                            size="small"
                            disabled={isStatusDiferenteRevertido}
                            value={percentualNotaRoyaltiesSazonal}
                            onChange={(_e, value) => setFieldValue('percentualNotaRoyaltiesSazonal', value)}
                        />
                    </span>
                </Tooltip>
            </Block>

            <Block>
                <SubTitle label="Configurações automáticas" />

                <Stack direction="row" sx={{ gap: 2 }}>
                    <Tooltip title={tooltipGerarConfiguracoes || ''}>
                        <span>
                            <Button
                                sx={{ width: 180 }}
                                variant="outlined"
                                color="success"
                                onClick={gerarConfiguracoes}
                                loading={isLoadingGerar}
                                disabled={disableBtnGerar}
                            >
                                Gerar Configurações
                            </Button>
                        </span>
                    </Tooltip>

                    <Button
                        variant="contained"
                        color="inherit"
                        onClick={reverterConfiguracoes}
                        loading={isLoadingReverter}
                        disabled={isLoadingGerar || isStatusNaoFinalizado}
                    >
                        Reverter Geração de Configurações
                    </Button>
                </Stack>

                {statusGeracao && (
                    <Chip
                        label={statusGeracao.dsStatusGeracao}
                        variant="outlined"
                        size="small"
                        color={statusGeracao.colorStatusGeracao}
                        sx={{ width: 180, mt: 1.5 }}
                    />
                )}
            </Block>
        </>
    );
}
